'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

// walrus
import Bugsnag from '@lib/bugsnag'

// lib
import ErrorFallback from './error-fallback'

/* -----------------------------------------------------------------------------
 * ErrorBoundary
 *
 * The boundary inside common's `Root` renders a fallback that reads the redux
 * session, which no longer exists here: it throws while handling the first
 * error and the app mounts nothing at all, with the original error nowhere in
 * sight. This one sits inside `Root`, so it catches first, and it reports what
 * actually broke. It goes away with common's UI kit.
 * -------------------------------------------------------------------------- */

export type ErrorBoundaryProps = {
  children: React.ReactNode
}

export type ErrorBoundaryState = {
  error: Error | null
  componentStack: string | null
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // keep the original in the console: it is the one worth reading
    console.error('Unhandled error', error, info.componentStack)
    this.setState({ componentStack: info.componentStack })
    Bugsnag.notify(error)
  }

  render() {
    const { error, componentStack } = this.state

    if (!error) {
      return this.props.children
    }

    return <ErrorFallback error={error} componentStack={componentStack} />
  }
}

export default ErrorBoundary
